import { useCallback, useState } from "react";

/** A reference image the user just attached, awaiting an answer on whether it
 *  should also join the asset library. */
export interface PendingAssetImage {
  file: File;
  /** `data:` URL of the same bytes, already read for the reference pill. */
  previewUrl: string;
}

export interface AddAssetPromptState {
  /** Non-null while the prompt is showing. */
  pending: PendingAssetImage | null;
  /** Project the asset is filed under when saved. */
  projectId: string | null | undefined;
  /** Argument order matches `useImageIntake`'s `onImageSelect`. */
  promptToSave: (previewUrl: string, file: File) => void;
  dismiss: () => void;
}

/** Offers a freshly attached reference image to the asset library.
 *
 *  The offer is advisory: the reference image is already attached to the
 *  prompt either way, so skipping or dismissing changes nothing about the
 *  generation that follows. */
export function useAddAssetPrompt(
  projectId: string | null | undefined,
): AddAssetPromptState {
  const [pending, setPending] = useState<PendingAssetImage | null>(null);

  const promptToSave = useCallback((previewUrl: string, file: File) => {
    setPending({ file, previewUrl });
  }, []);

  const dismiss = useCallback(() => setPending(null), []);

  return { pending, projectId, promptToSave, dismiss };
}
